/**
 * TrustFooter (v4.0 patch §15) — Trust + Safety infrastructure.
 *
 * Persistent footer mounted at the app shell. Surfaces:
 *   - educational disclaimer (not a diagnostic authority)
 *   - escalation strip (988 / 911 / 211)
 *   - audience identity line (Community / Student / Clinician)
 *
 * Visible on every page so the platform never drifts into looking like
 * a clinical product.
 */
import { useAudience } from "../lib/audience";

const QUICK_NUMBERS = [
  { tel: "988", label: "988 · Suicide & Crisis Lifeline", note: "free, 24/7" },
  { tel: "911", label: "911 · Emergency",                 note: "US emergency" },
  { tel: "211", label: "211 · Local resource navigator",  note: "food / housing / health" },
];

const TAGLINE = {
  community: "Clinical reasoning, finally connected.",
  student:   "Clinical reasoning, finally connected.",
  clinician: "Clinical reasoning, finally connected.",
};

const ACCENT = {
  community: "#10B981",
  student:   "#6B8F71",
  clinician: "#6B8F71",
};

export default function TrustFooter() {
  const { audience } = useAudience();
  const id = audience || "community";
  const tagline = TAGLINE[id] || TAGLINE.community;
  const accent  = ACCENT[id]  || ACCENT.community;

  return (
    <footer
      className="border-t border-white/8 bg-white/[0.03] mt-12"
      data-testid="trust-footer"
    >
      <div className="max-w-7xl mx-auto px-6 py-8 sm:py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Identity */}
          <div>
            <p
              className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
              style={{ color: accent }}
            >
              {id} mode
            </p>
            <p className="font-heading text-base text-white tracking-tight">
              Maldek<span className="text-[#6B8F71]"> by Hill</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-1" data-testid="footer-descriptor">
              A Hill Clinical Learning System
            </p>
            <p className="text-xs text-white/55 mt-1.5 leading-relaxed">
              {tagline}
            </p>
          </div>

          {/* Crisis numbers */}
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#D96C5B] mb-2">
              In an emergency
            </p>
            <ul className="space-y-1.5" data-testid="trust-footer-numbers">
              {QUICK_NUMBERS.map((n) => (
                <li key={n.tel} className="text-xs">
                  <a
                    href={`tel:${n.tel}`}
                    className="text-white hover:text-[#6B8F71] transition-colors"
                    data-testid={`trust-call-${n.tel}`}
                  >
                    {n.label}
                  </a>{" "}
                  <span className="text-white/45">· {n.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
